import {useMemo} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import AtomHelpText from '@s-ui/react-atom-help-text'
import Injector from '@s-ui/react-primitive-injector'
import PolymorphicElement from '@s-ui/react-primitive-polymorphic-element'
import {filterBasicAllSelectionsId} from '@adv-mt/mt-filter-basic'

import FilterWrapperBasicLegendWrapper from './components/LegendWrapper/index.js'
import {getBaseElementType, getNormalizedFilters, getNormalizedValue, getParsedValue} from './utils/filterUtils.js'
import {generateUniqueId, parseUniqueId} from './utils/idUtils.js'
import {BASE_CLASS_NAME, BASE_ELEMENTS_TYPES, NO_OP, TYPES, WRAPPERS} from './config.js'

const FilterWrapperBasic = ({
  description,
  design,
  disabled,
  filters = [],
  group,
  helpText,
  legend,
  legendIcon,
  onBlur,
  onChange,
  onFocus,
  onReset = NO_OP,
  overlayContentRef,
  placeholder,
  // TODO: create a config for Shape prop to FilterWrapperBasic: https://jira.ets.mpi-internal.com/browse/MTR-100229
  shape,
  showLegend = false,
  value,
  withRemoveSelections = true,
  wrapper
}) => {
  const Wrapper = WRAPPERS[wrapper]

  const normalizedValue = useMemo(() => getNormalizedValue({group, value}), [group, value])

  const normalizedFilters = useMemo(() => getNormalizedFilters({filters, group}), [filters, group])

  const baseElementType = getBaseElementType({filters, wrapper})

  const hasFieldSet = baseElementType === BASE_ELEMENTS_TYPES.FIELDSET

  const baseClassName = cx(
    BASE_CLASS_NAME,
    design && `${BASE_CLASS_NAME}--${design}`,
    hasFieldSet && `${BASE_CLASS_NAME}-fieldset`
  )

  const handleChange = ({value, ...rest}) =>
    onChange({
      value: getParsedValue({value}),
      ...rest
    })

  return (
    <PolymorphicElement as={baseElementType} className={baseClassName}>
      {hasFieldSet && (
        <FilterWrapperBasicLegendWrapper
          description={description}
          icon={legendIcon}
          id={group}
          legend={legend}
          showLegend={showLegend}
        />
      )}
      <Injector
        disabled={disabled}
        filters={normalizedFilters}
        id={group}
        group={group}
        label={legend}
        onBlur={onBlur}
        onChange={handleChange}
        onFocus={onFocus}
        onReset={onReset}
        overlayContentRef={overlayContentRef}
        placeholder={placeholder}
        shape={shape}
        showLabel={showLegend}
        value={normalizedValue}
        withRemoveSelections={withRemoveSelections}
      >
        {Wrapper}
      </Injector>
      {helpText && <AtomHelpText text={helpText} />}
    </PolymorphicElement>
  )
}

FilterWrapperBasic.displayName = 'FilterWrapperBasic'
FilterWrapperBasic.propTypes = {
  description: PropTypes.string,
  design: PropTypes.string,
  disabled: PropTypes.bool,
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      allSelectionsId: PropTypes.string,
      allSelectionsLabel: PropTypes.string,
      helpText: PropTypes.string,
      label: PropTypes.string,
      name: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          extendedInfo: PropTypes.string,
          icon: PropTypes.node,
          id: PropTypes.string,
          isDisabled: PropTypes.bool,
          isHighlighted: PropTypes.bool,
          label: PropTypes.string,
          nestedItems: PropTypes.arrayOf(
            PropTypes.shape({
              extendedInfo: PropTypes.string,
              icon: PropTypes.node,
              id: PropTypes.string,
              isHighlighted: PropTypes.bool,
              label: PropTypes.string
            })
          )
        })
      ),
      placeholder: PropTypes.string
    })
  ),
  group: PropTypes.string,
  helpText: PropTypes.string,
  legend: PropTypes.string,
  legendIcon: PropTypes.node,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onReset: PropTypes.func,
  overlayContentRef: PropTypes.node,
  placeholder: PropTypes.string,
  shape: PropTypes.string,
  showLegend: PropTypes.bool,
  value: PropTypes.object,
  withRemoveSelections: PropTypes.bool,
  wrapper: PropTypes.string
}

export {
  filterBasicAllSelectionsId as filterWrapperBasicAllSelectionsId,
  TYPES as filterWrapperBasicTypes,
  generateUniqueId as filterWrapperBasicGenerateUniqueId,
  parseUniqueId as filterWrapperBasicParseUniqueId
}

export default FilterWrapperBasic
